import { FormHelperText, InputLabel, Stack, type SxProps } from '@mui/material';
import { type ReactNode } from 'react';

import { isDefined } from '../../utils/types.js';

interface FormFieldBaseProps {
  id: string;
  labelAdornment?: ReactNode;
  children: ReactNode;
  helperText?: ReactNode;
  /**
   * If error is provided, it will be used instead of `helperText` to display error message.
   */
  error?: ReactNode;
  /** Marks the label with MUI's required indicator. Does not enforce anything on its own. */
  required?: boolean;
  sx?: SxProps;
}
/**
 * In order to guide people towards accessibility informs, we are requiring either `label` or `aria-label`
 * This helps guide people to add a label or aria-label, and that they can't skip it.
 * We can't guarantee it in this component, but this should provide a nudge towards that.
 * See also:
 * https://notable-health.atlassian.net/wiki/spaces/notableengineering/pages/2552397847/Accessibility+Guidelines+For+Engineers
 */
type RequireLabelOrAria =
  | { label: ReactNode; 'aria-label'?: never }
  | { label?: never; 'aria-label': string };

export type FormFieldProps = FormFieldBaseProps & RequireLabelOrAria;

export function FormField(props: FormFieldProps) {
  const {
    id,
    error,
    label,
    'aria-label': ariaLabel,
    labelAdornment,
    children,
    helperText,
    required,
    ...stackProps
  } = props;
  const isError = Boolean(error);
  const displayedHelperText = error ?? helperText;

  return (
    <Stack {...stackProps} spacing={0.5}>
      <Stack direction="row" spacing={0.5} justifyContent="flex-start" alignItems="center">
        {isDefined(label) && (
          <InputLabel htmlFor={id} error={isError} required={required}>
            {label}
          </InputLabel>
        )}
        {labelAdornment}
      </Stack>
      {children}
      {displayedHelperText && (
        <FormHelperText error={isError}>{displayedHelperText}</FormHelperText>
      )}
    </Stack>
  );
}
